import { Address, BzAddress } from '@breezy/shared'
import { getConfig } from '../config'
import { rumError } from './rum'

const { mapsAPIKey } = getConfig()

/**
 * Structured address extracted from Google Places address_components.
 * Used to bypass the addresser library which fails on highway/period addresses.
 */
export type GooglePlaceAddress = {
  line1: string
  line2?: string
  city: string
  stateAbbreviation: string
  zipCode: string
}

/**
 * Finds a specific component type from Google Places address_components array.
 */
const findAddressComponent = (
  components: google.maps.GeocoderAddressComponent[],
  type: string,
): google.maps.GeocoderAddressComponent | undefined => {
  return components.find(component => component.types.includes(type))
}

/**
 * Extracts structured address from Google Places address_components.
 * This avoids the addresser library which fails on addresses with periods (e.g., U.S. 93).
 *
 * @param placeId - Google Place ID from autocomplete selection
 * @param placesService - Google Places Service instance
 * @returns Structured address or null if essential components are missing
 */
export const getPlaceAddressById = async (
  placeId: string,
  placesService: google.maps.places.PlacesService,
): Promise<GooglePlaceAddress | null> => {
  return new Promise(resolve => {
    try {
      placesService.getDetails(
        {
          placeId,
          fields: ['address_components'],
        },
        details => {
          const components = details?.address_components
          if (!components || components.length === 0) {
            return resolve(null)
          }

          const streetNumber = findAddressComponent(components, 'street_number')
          const route = findAddressComponent(components, 'route')
          const subpremise = findAddressComponent(components, 'subpremise')
          // Try locality first, fall back to sublocality for rural areas
          const city = findAddressComponent(components, 'locality') || findAddressComponent(components, 'sublocality')
          const state = findAddressComponent(components, 'administrative_area_level_1')
          const postalCode = findAddressComponent(components, 'postal_code')

          // Street and postal code are required
          if (!route || !postalCode) {
            return resolve(null)
          }

          // Build line1: street number + route (e.g., "6328 US-93 S")
          // Use short_name for route to match the abbreviated form shown in autocomplete dropdown
          const line1Parts: string[] = []
          if (streetNumber?.short_name) {
            line1Parts.push(streetNumber.short_name)
          }
          line1Parts.push(route.short_name)
          const line1 = line1Parts.join(' ')

          // line2 is optional (apartment, unit, suite)
          const line2 = subpremise?.long_name

          // City and state are required for a valid address
          if (!city || !state) {
            return resolve(null)
          }

          return resolve({
            line1,
            line2,
            city: city.long_name,
            stateAbbreviation: state.short_name,
            zipCode: postalCode.long_name,
          })
        },
      )
    } catch (e) {
      rumError('google_places_error', e, { operation: 'getPlaceAddressById', placeId })
      return resolve(null)
    }
  })
}

/**
 * @deprecated Use getPlaceAddressById to instead to extract the complete address
 *
 */
export const getPlacePostCodeById = async (
  placeId: string,
  placesService: google.maps.places.PlacesService,
): Promise<string> => {
  return new Promise((resolve, _) => {
    try {
      placesService.getDetails(
        {
          placeId,
          fields: ['address_components'],
        },
        details => {
          let postcode = ''
          details?.address_components?.forEach(entry => {
            if (entry.types?.[0] === 'postal_code') {
              postcode = entry.long_name
            }
          })
          return resolve(postcode)
        },
      )
    } catch (e) {
      console.error(e)
      return resolve('')
    }
  })
}

export const getGoogleMapScreenshotSrc = (address: Address) =>
  'https://maps.googleapis.com/maps/api/staticmap?center=' +
  encodeURIComponent(
    address.line1 + ', ' + address.city + ', ' + address.stateAbbreviation + ' ' + BzAddress.getZip5(address),
  ) +
  '&zoom=15&size=400x250&maptype=roadmap&markers=color:red%7Clabel:A%7C' +
  `&key=${mapsAPIKey}`
